import { queryKey } from '../constants/queryClientKey';
import {
  createLayout,
  deleteLayout,
  getLayouts,
  ParamsCreateLayout,
  ParamsUpdateLayout,
  updateLayout,
} from '../apis/layout.api';
import { defaultGetNextPageParam, sortObjectKeys } from '@vindr/utils/helpers/common.helper';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

export function useGetLayouts(params: Parameters<typeof getLayouts>[0], options?: UseQueryOptions) {
  return useQuery([queryKey.layout_report, sortObjectKeys(params)], () => getLayouts(params), {
    ...options,
  }) as UseQueryResult<Awaited<ReturnType<typeof getLayouts>>>;
}

export function useInfinityGetLayouts(
  params: Parameters<typeof getLayouts>[0],
  options?: UseInfiniteQueryOptions
) {
  return useInfiniteQuery(
    [queryKey.account, sortObjectKeys(params)],
    (e) =>
      getLayouts({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getLayouts>>>;
}

export function useCreateLayout() {
  const queryClient = useQueryClient();

  return useMutation(async (params: ParamsCreateLayout) => createLayout(params), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.layout_report]);
    },
    onError: (err) => {
      console.error('useCreateLayout:', err);
    },
  });
}

export function useUpdateLayout() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateLayout }) => updateLayout(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.layout_report]);
      },

      onError: (err) => {
        console.error('useUpdateLayout:', err);
      },
    }
  );
}

export function useDeleteLayout() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteLayout(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.layout_report]);
    },
    onError: (err) => {
      console.error('useDeleteLayout:', err);
    },
  });
}
